/**
 * PredictionScorecardModal
 *
 * Multi-location modal for the Prediction Scorecard (Reports → Executive).
 * Always covers all locations for the selected year; runs automatically on
 * open since there is nothing else to configure. Patterns the
 * TsmRankingReportModal — retry on transient errors, print-to-PDF + Excel
 * export via the shared report-export utilities.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { ReportDefinition } from '../../types/reports';
import { ReportExportButtons } from './ReportExportButtons';
import { printCurrentPage } from '../../utils/reportExport';
import { loadExcelJS } from '../../utils/loadExcelJS';
import { runWholesaleComparison, RunReportResponse } from '../../services/api';
import { PredictionScorecard, PredictionScorecardData } from './renderers/PredictionScorecard';

// All rooftops — the scorecard covers every location with saved projections
// (the executor reports the rest as not scorable).
const ALL_LOCATION_IDS = [1, 2, 3, 4, 5, 10, 11, 12, 14, 44];

const FIRST_SCORECARD_YEAR = 2026;

const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

interface PredictionScorecardModalProps {
  report: ReportDefinition;
  isOpen: boolean;
  onClose: () => void;
}

type ReportStatus = 'idle' | 'running' | 'complete' | 'error';

export const PredictionScorecardModal: React.FC<PredictionScorecardModalProps> = ({
  report,
  isOpen,
  onClose,
}) => {
  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState(Math.max(FIRST_SCORECARD_YEAR, currentYear));
  const [status, setStatus] = useState<ReportStatus>('idle');
  const [error, setError] = useState<string | null>(null);
  const [reportData, setReportData] = useState<RunReportResponse | null>(null);

  const yearOptions = useMemo(() => {
    const years: number[] = [];
    for (let y = Math.max(FIRST_SCORECARD_YEAR, currentYear); y >= FIRST_SCORECARD_YEAR; y--) years.push(y);
    return years;
  }, [currentYear]);

  const isRetryableError = (err: unknown): boolean => {
    const msg = err instanceof Error ? err.message.toLowerCase() : '';
    return msg.includes('failed to fetch') || msg.includes('networkerror') ||
      msg.includes('timeout') || msg.includes('504') || msg.includes('503') ||
      msg.includes('network') || msg.includes('aborted') || msg.includes('timed out');
  };

  const runReport = async (year: number) => {
    setStatus('running');
    setError(null);
    setReportData(null);

    const MAX_ATTEMPTS = 2;
    let lastError: unknown;

    try {
      for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
        try {
          const response = await runWholesaleComparison(
            report.id,
            ALL_LOCATION_IDS,
            year,
            new Date().getMonth() + 1, // required by the route; the executor scores by calendar internally
          );
          if (!response.success || !response.data) {
            throw new Error(response.error || 'Failed to generate report');
          }
          setReportData(response.data);
          setStatus('complete');
          return;
        } catch (err) {
          lastError = err;
          if (attempt < MAX_ATTEMPTS && isRetryableError(err)) {
            console.warn(`Prediction Scorecard attempt ${attempt} failed, retrying...`, err);
            continue;
          }
          throw err;
        }
      }
    } catch {
      setError(lastError instanceof Error ? lastError.message : 'Failed to generate report');
      setStatus('error');
    }
  };

  // Nothing to configure — run automatically when the modal opens or the year changes.
  useEffect(() => {
    if (isOpen) runReport(selectedYear);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, selectedYear]);

  const handleExportExcel = async () => {
    if (!reportData?.data) return;
    const data = reportData.data as PredictionScorecardData;

    // Lazy-load exceljs only on click (see utils/loadExcelJS.ts for the
    // stale-chunk self-heal this wrapper provides).
    const ExcelJS = await loadExcelJS();
    const wb = new ExcelJS.Workbook();
    wb.creator = "Darling's Dashboard";
    wb.created = new Date();

    const ws = wb.addWorksheet('Prediction Scorecard', {
      views: [{ state: 'frozen', xSplit: 0, ySplit: 8 }],
      pageSetup: { orientation: 'landscape', fitToPage: true, fitToWidth: 1, fitToHeight: 0 },
    });

    // ── Header + standings (rows 1-6) ──
    ws.getCell('A1').value = `Prediction Scorecard — ${data.year}`;
    ws.getCell('A1').font = { size: 16, bold: true, color: { argb: 'FF1F2937' } };
    ws.mergeCells('A1:P1');
    ws.getCell('A2').value =
      `${data.scoredMonths.length} of 12 months scored · GM projections vs Model A (Run-Rate + Trend) and Model B (Holt-Winters), trained on ${data.year - 3}–${data.year - 1} only`;
    ws.getCell('A2').font = { size: 10, color: { argb: 'FF4B5563' } };
    ws.mergeCells('A2:P2');

    ws.getCell('A4').value = 'Standings (weighted gross $ error — lower wins)';
    ws.getCell('A4').font = { size: 11, bold: true };
    ws.mergeCells('A4:P4');
    data.standings.forEach((c, i) => {
      const r = ws.getRow(5 + i);
      r.getCell(1).value = c.label + (data.leader === c.id ? '  ◀ leading' : '');
      r.getCell(1).font = { bold: data.leader === c.id };
      r.getCell(4).value = c.grossErrorPct;
      r.getCell(4).numFmt = '0.0%';
      r.getCell(5).value = `${c.monthlyWins} wins`;
      r.getCell(6).value = c.unitsErrorPct;
      r.getCell(6).numFmt = '0.0%';
      r.getCell(7).value = 'units error';
    });

    // ── Flat table (from row 8): one row per contender/actual per series ──
    const headerRow = ws.getRow(8);
    const headers = ['Location', 'Department', 'Metric', 'Series', ...MONTH_LABELS];
    headers.forEach((h, i) => {
      const cell = headerRow.getCell(i + 1);
      cell.value = h;
      cell.font = { bold: true, size: 10, color: { argb: 'FFFFFFFF' } };
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF374151' } };
      cell.alignment = { horizontal: i <= 3 ? 'left' : 'right', vertical: 'middle' };
    });
    headerRow.height = 20;

    let rowIdx = 9;
    for (const loc of data.locations) {
      for (const s of loc.series) {
        const numFmt = s.kind === 'GROSS' ? '"$"#,##0' : '#,##0';
        const rows: Array<{ label: string; values: (number | null)[] | null; winnerId?: 'GM' | 'A' | 'B' }> = [
          { label: 'GM Projection', values: s.gm, winnerId: 'GM' },
          { label: 'Model A', values: s.a, winnerId: 'A' },
          { label: 'Model B', values: s.b, winnerId: 'B' },
          { label: 'Actual', values: s.actuals },
        ];
        for (const row of rows) {
          const r = ws.getRow(rowIdx++);
          r.getCell(1).value = loc.name;
          r.getCell(2).value = s.dept;
          r.getCell(3).value = s.metric;
          r.getCell(4).value = row.label;
          r.getCell(4).font = { bold: row.label === 'Actual' };
          for (let m = 0; m < 12; m++) {
            const cell = r.getCell(5 + m);
            cell.value = row.values ? row.values[m] : null;
            cell.numFmt = numFmt;
            cell.alignment = { horizontal: 'right' };
            if (row.winnerId && s.winners[m] === row.winnerId) {
              cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFD1FAE5' } };
              cell.font = { bold: true, color: { argb: 'FF065F46' } };
            }
          }
        }
      }
    }

    ws.autoFilter = { from: { row: 8, column: 1 }, to: { row: rowIdx - 1, column: 16 } };
    const widths = [30, 14, 12, 14, ...new Array(12).fill(11)];
    widths.forEach((w, i) => { ws.getColumn(i + 1).width = w; });

    const buf = await wb.xlsx.writeBuffer();
    const blob = new Blob([buf], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `Prediction-Scorecard-${data.year}.xlsx`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleClose = () => {
    setStatus('idle');
    setError(null);
    setReportData(null);
    onClose();
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) handleClose();
  };

  if (!isOpen) return null;

  const hasResults = status === 'complete';

  return (
    <div
      className="fixed inset-0 z-50 overflow-hidden flex items-center justify-center p-4 print:p-0 print:overflow-visible print:static"
      onClick={handleBackdropClick}
    >
      <div className="absolute inset-0 bg-black bg-opacity-50 transition-opacity print:hidden" />
      <div className="relative max-w-7xl w-full max-h-[95vh] bg-white rounded-lg shadow-xl flex flex-col print:max-w-none print:max-h-none print:rounded-none print:shadow-none">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between print:border-b-2">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">{report.title}</h2>
            <p className="text-sm text-gray-500 mt-1">
              GM projections vs two statistical models, scored against actuals as each month closes — all locations
            </p>
          </div>
          <div className="flex items-center gap-3">
            {yearOptions.length > 1 && (
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(parseInt(e.target.value, 10))}
                disabled={status === 'running'}
                className="block rounded-md border-gray-300 shadow-sm focus:border-amber-500 focus:ring-amber-500 text-sm print:hidden"
              >
                {yearOptions.map(y => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            )}
            <button
              onClick={handleClose}
              className="p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 print:hidden"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 print:overflow-visible">
          <div id="report-print-content">
            <div className="hidden print:block mb-4">
              <h1 className="text-xl font-bold">Prediction Scorecard — {selectedYear}</h1>
              <p className="text-sm text-gray-600">
                GM projections vs Model A (Run-Rate + Trend) and Model B (Holt-Winters) · Darling's Auto Group
              </p>
            </div>

            {status === 'running' && (
              <div className="text-center py-12">
                <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-gray-200 border-t-amber-600 mb-4" />
                <p className="text-gray-500">Scoring predictions against the books...</p>
              </div>
            )}

            {status === 'error' && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-red-700">{error || 'An error occurred while generating the report.'}</p>
                <button
                  onClick={() => runReport(selectedYear)}
                  className="mt-2 text-sm text-red-600 hover:text-red-800 underline"
                >
                  Try again
                </button>
              </div>
            )}

            {status === 'complete' && reportData && (
              <PredictionScorecard data={reportData.data as PredictionScorecardData} />
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex items-center justify-between print:hidden">
          <ReportExportButtons
            onPrintPDF={printCurrentPage}
            onExportExcel={handleExportExcel}
            disabled={!hasResults}
          />
          <button
            onClick={handleClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};
